import React, { createContext, useEffect, useState, ReactNode } from 'react';

import {
  environmentService,
  EnvironmentConfig,
} from '../services/environmentService';
import { LoadingSpinner } from '../components/LoadingSpinner';

interface EnvironmentContextType {
  config: EnvironmentConfig | null;
  loading: boolean;
  error: string | null;
}

const EnvironmentContext = createContext<EnvironmentContextType | undefined>(
  undefined
);

interface EnvironmentProviderProps {
  children: ReactNode;
}

export const EnvironmentProvider: React.FC<EnvironmentProviderProps> = ({
  children,
}) => {
  const [config, setConfig] = useState<EnvironmentConfig | null>(
    environmentService.getCachedConfig()
  );
  const [loading, setLoading] = useState(!environmentService.isConfigLoaded());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // If config is already loaded, nothing to do
    if (environmentService.isConfigLoaded()) {
      setConfig(environmentService.getCachedConfig());
      setLoading(false);
      return;
    }

    // Load config
    const loadConfig = async () => {
      try {
        setLoading(true);
        setError(null);

        const loadedConfig = await environmentService.getConfig();
        setConfig(loadedConfig);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Unknown error occurred';
        setError(errorMessage);
        console.error('Failed to load environment config:', err);

        // Don't block the app - let it continue with fallback config
        // The environmentService will have already provided fallback values
        setConfig(environmentService.getCachedConfig());
      } finally {
        setLoading(false);
      }
    };

    loadConfig();
  }, []);

  // Show loading screen while config is loading
  if (loading) {
    return (
      <LoadingSpinner
        title="Loading UM Tools"
        message="Initializing application..."
      />
    );
  }

  // Continue with app even if there's an error (fallback config will be used)
  return (
    <EnvironmentContext.Provider value={{ config, loading, error }}>
      {children}
    </EnvironmentContext.Provider>
  );
};
